import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Upload, Trash2, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// The API body limit is ~100KB, so the logo is stored as a compact data URL.
const MAX_DATA_URL_BYTES = 90 * 1024;

const PROFILE_FIELDS = {
  business_name: "",
  business_address: "",
  business_phone: "",
  business_city: "",
  business_state: "",
  business_pincode: "",
};

async function fileToLogoDataUrl(file) {
  const rawUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("read failed"));
    reader.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("not an image"));
    image.src = rawUrl;
  });

  const drawAt = (maxSide, type, quality) => {
    const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (type === "image/jpeg") {
      ctx.fillStyle = "#ffffff"; // JPEG has no alpha — flatten on white
      ctx.fillRect(0, 0, w, h);
    }
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL(type, quality);
  };

  // PNG keeps transparency; step down until it fits
  for (const side of [400, 300, 220, 160]) {
    const out = drawAt(side, "image/png");
    if (out.length <= MAX_DATA_URL_BYTES) return out;
  }
  // Photo-like logo — JPEG compresses far better
  for (const side of [400, 300, 220]) {
    const out = drawAt(side, "image/jpeg", 0.82);
    if (out.length <= MAX_DATA_URL_BYTES) return out;
  }
  throw new Error("image too complex");
}

const FieldInput = ({ id, label, value, onChange, placeholder, className = "" }) => (
  <div className={className}>
    <Label htmlFor={id} className="text-xs font-semibold text-slate-600 dark:text-slate-300">{label}</Label>
    <Input
      id={id}
      data-testid={`company-${id}`}
      value={value}
      placeholder={placeholder}
      onChange={onChange}
      className="mt-1 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl dark:text-white"
    />
  </div>
);

const CompanyProfileCard = ({ businessId }) => {
  const [logoUrl, setLogoUrl] = useState("");
  const [profile, setProfile] = useState(PROFILE_FIELDS);
  const [loading, setLoading] = useState(true);
  const [savingLogo, setSavingLogo] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const fileRef = useRef(null);
  const qs = businessId ? `?business_id=${businessId}` : "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${BACKEND_URL}/api/billing/settings${businessId ? `?business_id=${businessId}` : ""}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : {}))
      .then((s) => {
        if (cancelled) return;
        setLogoUrl(s?.logo_url || "");
        setProfile({
          business_name: s?.business_name || "",
          business_address: s?.business_address || "",
          business_phone: s?.business_phone || "",
          business_city: s?.business_city || "",
          business_state: s?.business_state || "",
          business_pincode: s?.business_pincode || "",
        });
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  const put = async (body) => {
    const res = await fetch(`${BACKEND_URL}/api/billing/settings${qs}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error();
  };

  const saveLogo = async (newUrl) => {
    setSavingLogo(true);
    try {
      await put({ logo_url: newUrl });
      setLogoUrl(newUrl);
      toast.success(newUrl ? "Company logo saved" : "Company logo removed");
    } catch {
      toast.error("Failed to save company logo");
    } finally {
      setSavingLogo(false);
    }
  };

  const saveProfile = async () => {
    if (!profile.business_name.trim()) {
      toast.error("Company name is required");
      return;
    }
    setSavingProfile(true);
    try {
      await put(profile);
      toast.success("Company details saved");
    } catch {
      toast.error("Failed to save company details");
    } finally {
      setSavingProfile(false);
    }
  };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const url = await fileToLogoDataUrl(file);
      await saveLogo(url);
    } catch {
      toast.error("Couldn't process that image — try a PNG or JPG logo");
    }
  };

  const setField = (key) => (e) => setProfile((p) => ({ ...p, [key]: e.target.value }));

  return (
    <section className="bg-white dark:bg-slate-800 rounded-3xl overflow-hidden border border-slate-100 dark:border-slate-700 shadow-xl shadow-amber-200/60 dark:shadow-none" data-testid="company-profile-card">
      <div className="bg-gradient-to-r from-amber-400 to-orange-500 px-5 py-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center shrink-0">
          <Building2 className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-white font-heading font-bold text-lg leading-tight">Company Profile</h2>
          <p className="text-white/80 text-xs mt-0.5">Details & branding shown on salary slips</p>
        </div>
      </div>
      <div className="p-5 space-y-5">
        {/* Logo */}
        <div className="flex items-center gap-4">
          <div className="w-28 h-20 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/40 flex items-center justify-center overflow-hidden shrink-0">
            {loading ? (
              <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
            ) : logoUrl ? (
              <img src={logoUrl} alt="Company logo" className="max-w-full max-h-full object-contain p-1" data-testid="company-logo-preview" />
            ) : (
              <span className="text-xs text-slate-400 dark:text-slate-500 text-center px-2">No logo yet</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={savingLogo || loading}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-4"
              data-testid="company-logo-upload"
            >
              {savingLogo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {logoUrl ? "Replace logo" : "Upload logo"}
            </Button>
            {logoUrl && (
              <Button
                onClick={() => saveLogo("")}
                disabled={savingLogo || loading}
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full px-4"
                data-testid="company-logo-remove"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove
              </Button>
            )}
          </div>
        </div>
        <p className="text-xs text-slate-400 dark:text-slate-500 -mt-2">PNG with transparent background works best. Large images are resized automatically.</p>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={onPick} />

        {/* Details */}
        <div className="border-t border-slate-100 dark:border-slate-700 pt-4 grid grid-cols-2 gap-3">
          <FieldInput id="business_name" label="Company Name *" value={profile.business_name} onChange={setField("business_name")} placeholder="e.g., Digital Nexus Media" className="col-span-2" />
          <FieldInput id="business_address" label="Address" value={profile.business_address} onChange={setField("business_address")} placeholder="Street / building / area" className="col-span-2" />
          <FieldInput id="business_phone" label="Phone No." value={profile.business_phone} onChange={setField("business_phone")} placeholder="e.g., 9876543210" />
          <FieldInput id="business_city" label="City" value={profile.business_city} onChange={setField("business_city")} placeholder="e.g., Bareilly" />
          <FieldInput id="business_state" label="State" value={profile.business_state} onChange={setField("business_state")} placeholder="e.g., Uttar Pradesh" />
          <FieldInput id="business_pincode" label="PIN Code" value={profile.business_pincode} onChange={setField("business_pincode")} placeholder="e.g., 243001" />
        </div>
        <Button
          onClick={saveProfile}
          disabled={savingProfile || loading}
          className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full font-semibold"
          data-testid="company-profile-save"
        >
          {savingProfile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Details
        </Button>
      </div>
    </section>
  );
};

export default CompanyProfileCard;
